/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useCustomerEmployees } from "~/hooks/useCustomerEmployees";
import { CreateApplicationData, RouteParams } from "~/models";
import { ACTIONS, Can } from "~/routing";
import { generateActionSuccessText, useAxios } from "~/tools";

type AddApplicationProps = {
  refetch: () => void;
};

function AddApplication({ refetch }: AddApplicationProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const { employees } = useCustomerEmployees();

  const state = useParams<RouteParams>();
  const { customerId } = state;

  const onSubmit = async (values: CreateApplicationData) => {
    setIsLoading(true);
    try {
      await Axios().post(`/applications`, {
        customerId,
        name: values.name,
        ownerId: values.ownerId,
      });
      refetch();
      showSuccess(generateActionSuccessText("Application")()("added")());
      setTimeout(() => {
        setIsSidebarOpen(false);
      }, 200);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  };

  const environment = process.env.REACT_APP_ENV || "";

  return (
    <>
      {environment !== "production" ? (
        <Can action={ACTIONS.ADD_APPLICATION}>
          <Button icon="plus" onClick={() => setIsSidebarOpen(true)}>
            Add Application
          </Button>
        </Can>
      ) : null}

      <SidebarForm
        title="Add Application"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="name"
          label="Name"
          placeholder="Application name"
          required
        />

        <SidebarDropdown
          name="ownerId"
          label="Owner"
          options={employees}
          required
        />

        <SidebarFormButtons
          submitEnabled={!!(watcher.name && watcher.ownerId)}
          submitLoading={isLoading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { AddApplication };
